import {
  AvailableCredit,
  EditWorksheetDto,
  FilteredCreditApplication,
  FilteredWorksheet,
  FinancingConfigDto,
  Merchant,
  useMerchantPaymentPlanById,
  useUpdateWorksheet,
} from '@applyonline/shared'
import { ProblemOccuredAlert, ScrollableAlert, Stepper, StepperStep } from '@applyonline/shared/components'
import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc, DateOnly } from '@if/core/date'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { AxiosError, HttpStatusCode } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Constants } from '../../types'
import FinalAmountStep from './components/FinalAmountStep'
import FirstPaymentStep from './components/FirstPaymentStep'
import InsuranceStep from './components/InsuranceStep'
import PlanSelectionStep from './components/PlanSelectionStep'
import SummaryStep from './components/SummaryStep'
import { DEFAULT_STEP, EWorksheetStep, useWorksheetStepIsValid } from './hooks'
import { useWorksheetSchema, WorksheetForm } from './schema-hooks'
interface Props {
  step?: EWorksheetStep
  setStep: (step: EWorksheetStep) => void
  creditApp: FilteredCreditApplication
  merchant: Merchant
  financingProgramConfig: FinancingConfigDto
  worksheet: FilteredWorksheet
  availableCredit: AvailableCredit
}

const WorksheetStepper = ({
  merchant,
  creditApp,
  step = EWorksheetStep.AmountRequested,
  setStep,
  financingProgramConfig,
  worksheet,
  availableCredit,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>('')
  const [showProblemOccured, setShowProblemOccured] = useState<boolean>(false)
  const [codePromo, setCodePromo] = useState<string | null>(null)
  const [appliedPaymentPlanId, setAppliedPaymentPlanId] = useState<string | null>(null)
  const [submitWorksheetUpdate, isSubmitting] = useUpdateWorksheet(EFinancingProgram.Personal)
  const currentDate = useMemo(() => DateOnly.today(), [])

  const handleSetStep = useCallback((s: string) => setStep(s as EWorksheetStep), [setStep])

  const canHaveProtection = creditApp.applicant.age <= 65

  const steps = useMemo<EWorksheetStep[]>(() => {
    const stepList = []
    stepList.push(EWorksheetStep.AmountRequested, EWorksheetStep.FirstPayment, EWorksheetStep.PayOverTime)
    if (canHaveProtection) stepList.push(EWorksheetStep.ProtectionPlan)
    stepList.push(EWorksheetStep.Summary)
    return stepList
  }, [canHaveProtection])

  const availableCreditAmount = availableCredit?.availableAmount ?? creditApp.finalDecision.maxAmountFinanced
  const amountRequestedDefaultValue = Math.min(
    worksheet.amountRequested,
    creditApp.finalDecision.maxAmountFinanced,
    availableCreditAmount,
  )

  const schema = useWorksheetSchema(
    availableCreditAmount,
    amountRequestedDefaultValue,
    worksheet.term,
    financingProgramConfig,
  )

  const form = useForm<WorksheetForm>({
    disabled: isSubmitting,
    mode: 'onChange',
    defaultValues: {
      firstPaymentOn: formatDate(worksheet.firstPaymentOn) || undefined,
      amountRequested: amountRequestedDefaultValue,
      term: creditApp.merchantPaymentPlan?.loanTerm || worksheet.term,
      includeInsurance: worksheet.includeInsurance,
      merchantPaymentPlanId: worksheet.paymentPlanId ?? creditApp.merchantPaymentPlanId,
    },
    resolver: yupResolver(schema),
  })

  // Anti skip step
  useWorksheetStepIsValid(step, steps, form.trigger)

  // Additional step validation
  useEffect(() => {
    if (!canHaveProtection && step === EWorksheetStep.ProtectionPlan)
      navigate(`../worksheet/${DEFAULT_STEP}`, { replace: true })
  }, [canHaveProtection, navigate, step])

  const merchantPaymentPlanId = form.watch('merchantPaymentPlanId')
  const regularInterestPlanId = EPaymentPlan.RegularDailyInterests as string
  const isRegularInterest =
    !merchantPaymentPlanId ||
    merchantPaymentPlanId === regularInterestPlanId ||
    !creditApp.merchantPaymentPlanId ||
    creditApp.merchantPaymentPlanId === regularInterestPlanId

  const [merchantPaymentPlan, isLoadingMerchantPaymentPlan] = useMerchantPaymentPlanById(
    merchant.id,
    isRegularInterest ? '' : merchantPaymentPlanId,
    creditApp.financingProgramId,
  )

  useEffect(() => {
    if (merchantPaymentPlan?.loanTerm) {
      form.setValue('term', merchantPaymentPlan.loanTerm, { shouldDirty: true, shouldValidate: true })
    }
  }, [form, merchantPaymentPlan?.loanTerm])

  const onFormSubmitted = useCallback(
    (data: WorksheetForm) => {
      if (!currentDate) {
        setError(t('worksheet.error.unavailableDelivery'))
        return
      }
      const nowUTC = new Date(`${currentDate.toLocaleDateString()}T00:00:00`)
      const expiry = new Date(creditApp.expiresOn)
      const receivedOnPlus90Days = new Date(creditApp.hardHitReportReceivedOn)
      receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)

      if (data.amountRequested > (availableCredit?.availableAmount ?? 0)) {
        setError(t('worksheet.error.amountRequestedHigherThanAvailableCredit'))
      } else if (compareAsc(currentDate, receivedOnPlus90Days) === 1)
        setError(t('worksheet.error.activationDateReport90Days'))
      else if (compareAsc(nowUTC, expiry) === 1) setError(t('worksheet.error.deliveryOnEarlierExpiration'))
      else {
        const ws: EditWorksheetDto = {
          ...worksheet,
          amountRequested: Number(data.amountRequested),
          term: data.term,
          firstPaymentOn: formatDate(data.firstPaymentOn),
          includeInsurance: data.includeInsurance,
          paymentFrequency: EPaymentFrequency.Monthly,
          deliveryOn: formatDate(currentDate),
          status: EWorksheetStatus.Active,
          creditApplicationId: creditApp.id,
          paymentPlanId: data.merchantPaymentPlanId || EPaymentPlan.RegularDailyInterests,
          create: false,
          versionTag: worksheet.versionTag,
        }
        submitWorksheetUpdate(ws)
          .then(() => {
            navigate(`/creditApplication/${creditApp.id}/contract`)
          })
          .catch((err: AxiosError) => {
            if (err.response?.status === HttpStatusCode.Conflict) setError(t('common.conflict'))
            else setShowProblemOccured(true)
          })
      }
    },
    [
      availableCredit?.availableAmount,
      creditApp.expiresOn,
      creditApp.hardHitReportReceivedOn,
      creditApp.id,
      currentDate,
      navigate,
      submitWorksheetUpdate,
      t,
      worksheet,
    ],
  )

  return (
    <main className="general-message">
      <ScrollableAlert showAlert={!!error} type="error" message={error} />
      <ProblemOccuredAlert showAlert={showProblemOccured} />
      <div className="form-section" style={{ width: '100%' }}>
        <FormProvider {...form}>
          <Stepper current={step} setCurrentStep={handleSetStep} isSubmitting={isSubmitting}>
            <StepperStep
              step={EWorksheetStep.AmountRequested}
              icon="hand-holding-dollar"
              label={t('worksheet.finalAmount.step')}
              hideControl={(availableCredit?.availableAmount ?? 0) < Constants.MinLoanAmount}
              render={({ setState }) => (
                <FinalAmountStep
                  creditApp={creditApp}
                  merchant={merchant}
                  setStepState={setState}
                  setCodePromo={setCodePromo}
                  setAppliedPaymentPlanId={setAppliedPaymentPlanId}
                  codePromo={codePromo}
                  maxAmountFinanced={creditApp.finalDecision.maxAmountFinanced}
                  availableCredit={availableCredit}
                />
              )}
            />
            <StepperStep
              step={EWorksheetStep.FirstPayment}
              icon="calendar-day"
              label={t('worksheet.firstPayment.step')}
              loading={isLoadingMerchantPaymentPlan}
              hideControl
              render={({ setState, setNext }) => (
                <FirstPaymentStep
                  setStepState={setState}
                  setNext={setNext}
                  soonestDeliveryOn={currentDate}
                  creditAppId={creditApp.id}
                />
              )}
            />
            <StepperStep
              step={EWorksheetStep.PayOverTime}
              icon="percent"
              label={t('worksheet.term.step')}
              hideControl
              render={({ setState, setNext, setPrevious }) => (
                <PlanSelectionStep
                  setStepState={setState}
                  setNext={setNext}
                  setPrevious={setPrevious}
                  creditApp={creditApp}
                  soonestDeliveryOn={currentDate}
                  promoTerm={merchantPaymentPlan?.loanTerm}
                  codePromo={codePromo}
                  appliedPaymentPlanId={appliedPaymentPlanId}
                />
              )}
            />
            {canHaveProtection && (
              <StepperStep
                step={EWorksheetStep.ProtectionPlan}
                icon="shield"
                label={t('worksheet.protection.step')}
                hideControl
                render={({ setState, setNext }) => (
                  <InsuranceStep
                    setStepState={setState}
                    setNext={setNext}
                    creditApp={creditApp}
                    soonestDeliveryOn={currentDate}
                    appliedPaymentPlanId={appliedPaymentPlanId}
                  />
                )}
              />
            )}
            <StepperStep
              step={EWorksheetStep.Summary}
              icon="memo-circle-check"
              label={t('worksheet.summary.step')}
              hideControl
              render={({ setState }) => (
                <SummaryStep
                  setStepState={setState}
                  setCurrentStep={handleSetStep}
                  creditApp={creditApp}
                  soonestDeliveryOn={currentDate}
                  onSubmit={form.handleSubmit(onFormSubmitted)}
                  merchantPaymentPlan={merchantPaymentPlan}
                  isSubmitting={isSubmitting}
                  appliedPaymentPlanId={appliedPaymentPlanId}
                />
              )}
            />
          </Stepper>
        </FormProvider>
      </div>
    </main>
  )
}

export default WorksheetStepper
